/**
 * Guest issue — the guest-order list, not a second model. The body is the
 * real orders the guest-create write rule already produced (origin ==
 * 'guest'), rendered through the same `<order-card>`/`<order-detail>` the
 * admin console's own Orders tab renders — so a guest ticket can never
 * visually or structurally drift from a sale.
 *
 * The list sits OUTSIDE any pending/settled/refused outcome switch: it is
 * always-visible page furniture, and `guestIssueOutcome` is a memory-tier
 * singleton that survives route teardown — wrapping the list in a `choose()`
 * over it would greet a returning operator with "Tickets issued" where the
 * list belongs. The verdict instead renders as a banner ABOVE the list, and
 * this view fires `guestIssueReset$` on connect so no stale verdict outlives
 * a round-trip.
 *
 * `eventId` is the host's pinned-event contract, mirroring <event-console>'s
 * own idiom: empty (the default) renders the event picker over
 * saleEventOptions, writing the choice to selectedSaleEventId; a non-empty
 * value means the host has already fixed the event — the console's own
 * Guests destination pins it — and no picker renders.
 *
 * No-access rendering belongs here, not to guest-issue.sync.ts — no
 * `.sync.ts` in this repo renders anything but an empty template. Reading
 * canViewSaleReports directly means one contract covers both hosts,
 * including the console's Guests destination, which is gated only on
 * canIssueGuestTickets and would otherwise leave a reports-less actor
 * staring at an empty list.
 */
import { SchmancyElement, schmancyElement } from "@mhmo91/schmancy/mixins";
import { show } from "@mhmo91/schmancy/overlay";
import type { Locale } from "@momo/i18n";
import { html, type TemplateResult } from "lit";
import { property, state as stateDecorator } from "lit/decorators.js";
import { choose } from "lit/directives/choose.js";
import { repeat } from "lit/directives/repeat.js";
import { when } from "lit/directives/when.js";
import { filter, take, takeUntil } from "rxjs";
import {
	consoleOrdersStatus,
	consoleTicketsListening,
	consoleViewModel,
	guestOrderRows,
} from "../../events/console/console.state.ts";
import "../../events/console/order-card.view.ts";
import { filterOrdersByQuery } from "../../events/console/order-detail.engine.ts";
import { LocalizeMixin } from "../../i18n/localize.ts";
import { organizationId } from "../../workspace/organization.state.ts";
import type { GuestAddDetail } from "./guest-issue-add.view.ts";
import "./guest-issue-add.view.ts";
import {
	guestIssueAction$,
	guestIssueLastGuest,
	guestIssueOutcome,
	guestIssueRefusalReason,
	guestIssueReset$,
	guestIssueResult$,
} from "./guest-issue.state.ts";
import {
	canViewSaleReports,
	saleCatalogStatus,
	saleEventOptions,
	saleTicketTypes,
	selectedSaleEventId,
} from "./sale.state.ts";

@schmancyElement("guest-issue")
export class GuestIssueView extends LocalizeMixin(SchmancyElement) {
	@property({ type: String }) eventId = "";
	@stateDecorator() private search = "";
	@stateDecorator() private writeError = "";
	@stateDecorator() private ticketLocale: Locale = "de";

	override connectedCallback(): void {
		super.connectedCallback();
		guestIssueReset$.next();
	}

	private readonly openAddGuest = (): void => {
		const eventId = selectedSaleEventId.value;
		if (!eventId) return;
		// Read as the dialog opens, never at submit: a switch made while it
		// stands open must not move the guest into another organization.
		const orgId = organizationId.value;
		guestIssueReset$.next();
		this.writeError = "";
		const event = saleEventOptions.get().find((o) => o.id === eventId) ?? null;
		show(
			() => html`<guest-issue-add
                .ticketTypes=${saleTicketTypes.value}
                .defaultLocale=${this.ticketLocale}
                .event=${event}
                .busy=${guestIssueOutcome.value === "pending"}
                .settled=${guestIssueOutcome.value === "settled"}
                .failure=${this.writeError || (guestIssueOutcome.value === "refused" ? this.t(guestIssueRefusalReason.value === "soldOut" ? "admin.sale.guestIssue.refusedBodySoldOut" : "admin.sale.guestIssue.refusedBodyGeneric") : "")}
                @issue=${(e: CustomEvent<GuestAddDetail>) => {
									const detail = e.detail;
									const requestId = crypto.randomUUID();
									this.ticketLocale = detail.locale;
									this.writeError = "";
									guestIssueLastGuest.set({
										name: detail.name,
										email: detail.email,
									});
									guestIssueResult$
										.pipe(
											filter((result) => result.requestId === requestId),
											take(1),
											takeUntil(this.disconnecting),
										)
										.subscribe((result) => {
											if (!result.ok) this.writeError = result.error;
										});
									guestIssueAction$.next({
										requestId,
										orgId,
										eventId,
										buyer: {
											email: detail.email,
											name: detail.name,
											locale: detail.locale,
										},
										lines: detail.lines,
									});
								}}
            ></guest-issue-add>`,
			{ ariaLabel: this.t("admin.sale.guestIssue.addDialogAriaLabel") },
		)
			.pipe(takeUntil(this.disconnecting))
			.subscribe();
	};

	override render(): TemplateResult {
		if (saleCatalogStatus.value === "loading") {
			return html`
				<div role="status" aria-busy="true" aria-label=${this.t("admin.sale.guestIssue.loadingAriaLabel")} class="flex flex-col gap-3 p-4 md:p-6">
					<schmancy-skeleton shape="text" class="w-1/2"></schmancy-skeleton>
					<schmancy-skeleton class="h-24 w-full rounded-2xl"></schmancy-skeleton>
					<schmancy-skeleton class="h-24 w-full rounded-2xl"></schmancy-skeleton>
				</div>
			`;
		}

		const options = saleEventOptions.get();
		if (options.length === 0) {
			return html`
				<div class="flex flex-col items-start gap-3 px-4 py-8 md:px-6">
					<schmancy-icon class="text-3xl text-surface-onVariant">event</schmancy-icon>
					<schmancy-typography type="title" token="sm">${this.t("admin.sale.guestIssue.noEventsTitle")}</schmancy-typography>
					<schmancy-typography type="body" token="sm" class="text-surface-onVariant max-w-xs">
						${this.t("admin.sale.guestIssue.noEventsBody")}
					</schmancy-typography>
				</div>
			`;
		}

		if (!canViewSaleReports.get()) {
			return html`
				<div class="flex flex-col items-start gap-2 px-4 py-8 md:px-6">
					<schmancy-icon class="text-3xl text-surface-onVariant">lock</schmancy-icon>
					<schmancy-typography type="body" token="sm" class="max-w-sm text-surface-onVariant">
						${this.t("admin.sale.guestIssue.noAccessBody")}
					</schmancy-typography>
				</div>
			`;
		}

		const types = saleTicketTypes.value;
		const orders = guestOrderRows.get();
		const filtered = filterOrdersByQuery(
			this.search,
			orders ?? [],
			consoleViewModel.get()?.ticketsByOrder,
		);

		return html`
			<section class="flex flex-col gap-4 p-4 md:p-6">
				<div class="flex flex-wrap items-center justify-between gap-3">
					<div class="flex items-center gap-3">
						<schmancy-typography type="title" token="md">${this.t("admin.sale.guestIssue.title")}</schmancy-typography>
						${when(
							orders !== null && orders.length > 0,
							() => html`
								<schmancy-typography type="label" token="sm" class="text-surface-onVariant">
									${this.t("admin.sale.guestIssue.issuedCountLabel", { count: orders?.length ?? 0 })}
								</schmancy-typography>
							`,
						)}
					</div>
					<div class="flex items-center gap-2">
						${when(
							orders !== null && orders.length > 0,
							() => html`
								<schmancy-input
									type="search"
									placeholder=${this.t(
										consoleTicketsListening.get()
											? "admin.sale.guestIssue.searchLabel"
											: "admin.sale.guestIssue.searchBuyerLabel",
									)}
									aria-label=${this.t(
										consoleTicketsListening.get()
											? "admin.sale.guestIssue.searchAriaLabel"
											: "admin.sale.guestIssue.searchBuyerAriaLabel",
									)}
									.value=${this.search}
									@change=${(e: CustomEvent<{ value: string }>) => {
										this.search = e.detail.value;
									}}
								></schmancy-input>
							`,
						)}
						<schmancy-button
							variant="filled"
							?disabled=${types.length === 0}
							title=${types.length === 0 ? this.t("admin.sale.guestIssue.noTypesBody") : ""}
							@click=${this.openAddGuest}
						>
							<schmancy-icon slot="prefix" aria-hidden="true">person_add</schmancy-icon>
							${this.t("admin.sale.guestIssue.addGuestButton")}
						</schmancy-button>
					</div>
				</div>

				${when(
					this.eventId === "" && options.length > 1,
					() => html`
						<schmancy-autocomplete
							label=${this.t("admin.sale.common.eventPickerLabel")}
							.value=${selectedSaleEventId.value ?? ""}
							@change=${(e: CustomEvent<{ value: string | string[] }>) => {
								const v = e.detail.value;
								const next = typeof v === "string" ? v : (v[0] ?? "");
								if (next === (selectedSaleEventId.value ?? "")) return;
								selectedSaleEventId.set(next || null);
							}}
						>
							${repeat(
								options,
								(o) => o.id,
								(o) =>
									html`<schmancy-option value=${o.id}>${o.name}</schmancy-option>`,
							)}
						</schmancy-autocomplete>
					`,
				)}

				${choose(guestIssueOutcome.value, [
					[
						"pending",
						() => html`
						<div class="flex items-center gap-3 rounded-2xl bg-surface-container p-4" role="status" aria-live="polite">
							<schmancy-spinner size="md"></schmancy-spinner>
							<schmancy-typography type="body" token="md">
								${this.t("admin.sale.guestIssue.pendingLabel", { name: guestIssueLastGuest.value.name })}
							</schmancy-typography>
						</div>
					`,
					],
					[
						"settled",
						() => html`
						<div class="flex items-center justify-between gap-3 rounded-2xl bg-surface-container p-4">
							<div class="flex items-center gap-3">
								<schmancy-icon class="text-2xl text-primary-default">check_circle</schmancy-icon>
								<schmancy-typography type="body" token="md">
									${this.t("admin.sale.guestIssue.settledBody", {
										name: guestIssueLastGuest.value.name,
										email: guestIssueLastGuest.value.email,
									})}
								</schmancy-typography>
							</div>
							<schmancy-icon-button
								icon="close"
								aria-label=${this.t("admin.sale.guestIssue.issueAnotherButton")}
								@click=${() => guestIssueReset$.next()}
							></schmancy-icon-button>
						</div>
					`,
					],
					[
						"refused",
						() => html`
						<div class="flex items-center justify-between gap-3 rounded-2xl bg-error-container p-4">
							<div class="flex items-center gap-3">
								<schmancy-icon class="text-2xl text-error-default">error</schmancy-icon>
								<schmancy-typography type="body" token="md">
									${
										guestIssueRefusalReason.value === "soldOut"
											? this.t("admin.sale.guestIssue.refusedBodySoldOut")
											: this.t("admin.sale.guestIssue.refusedBodyGeneric")
									}
								</schmancy-typography>
							</div>
							<schmancy-icon-button
								icon="close"
								aria-label=${this.t("admin.sale.guestIssue.tryAgainButton")}
								@click=${() => guestIssueReset$.next()}
							></schmancy-icon-button>
						</div>
					`,
					],
				])}

				${
					orders === null && consoleOrdersStatus.value === "error"
						? html`
								<schmancy-typography role="status" type="body" token="sm" class="max-w-sm py-8 text-error-default">
									${this.t("admin.events.console.ordersLoadFailed")}
								</schmancy-typography>
							`
						: orders === null
							? html`
								<div role="status" aria-busy="true" class="flex flex-col gap-2">
									<schmancy-visually-hidden>${this.t("admin.sale.guestIssue.loadingAriaLabel")}</schmancy-visually-hidden>
									<schmancy-skeleton aria-hidden="true" class="h-24 w-full rounded-2xl"></schmancy-skeleton>
									<schmancy-skeleton aria-hidden="true" class="h-24 w-full rounded-2xl"></schmancy-skeleton>
								</div>
							`
							: orders.length === 0
								? html`
								<div class="flex flex-col items-start gap-3 py-8">
									<schmancy-icon class="text-3xl text-surface-onVariant">confirmation_number</schmancy-icon>
									<schmancy-typography type="title" token="sm">${this.t("admin.sale.guestIssue.noGuestOrdersTitle")}</schmancy-typography>
									<schmancy-typography type="body" token="sm" class="text-surface-onVariant max-w-xs">
										${this.t("admin.sale.guestIssue.noGuestOrdersBody")}
									</schmancy-typography>
								</div>
							`
								: filtered.length === 0
									? html`
									<div class="flex flex-col items-start gap-3 py-8">
										<schmancy-icon class="text-3xl text-surface-onVariant">search_off</schmancy-icon>
										<schmancy-typography type="title" token="sm">${this.t("admin.sale.guestIssue.noMatchTitle")}</schmancy-typography>
										<schmancy-typography type="body" token="sm" class="text-surface-onVariant max-w-xs">
											${this.t("admin.sale.guestIssue.noMatchBody")}
										</schmancy-typography>
									</div>
								`
									: html`
									<div class="flex flex-col gap-2">
										${repeat(
											filtered,
											(o) => o.id,
											(o) => html`<order-card .order=${o}></order-card>`,
										)}
									</div>
								`
				}
			</section>
		`;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		"guest-issue": GuestIssueView;
	}
}
